import React from 'react';
import Description from '@mui/icons-material/Description';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Grid, Checkbox, FormControlLabel, Tooltip } from '@mui/material';
import { Card, Autocomplete, Typography, IconButton } from '@totalsoft/rocket-ui';
import { Prompt } from 'react-router-dom';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { Info } from '@mui/icons-material';

const StateTransitionComponent = ({
  stateTransition,
  transitionsCheck,
  documentStateList,
  textResourceCodeList,
  userGroups,
  onPropertyChanged,
  onUserGroupChanged,
  validation
}) => {
  const { t } = useTranslation();

  return (
    <Card icon={Description} iconColor='primary' title={t('DocumentTypes.DocumentStateTransition.GeneralInfo')}>
      <Prompt
        when={stateTransition.changed}
        message={t('DocumentTypes.DocumentStateTransition.LeavingWithoutSavingDocumentStateTransition')}
      />
      <Grid container spacing={2}>
        {transitionsCheck.sameTransitionExists && (
          <Grid item xs={12}>
            <Typography variant='subtitle1' color='error'>
              {t('DocumentTypes.DocumentStateTransition.SameTransitionExists')}
            </Typography>
          </Grid>
        )}
        {!isValid(validation) && (
          <Grid item xs={12}>
            <Typography variant='subtitle1' color='error'>
              {getErrors(validation)}
            </Typography>
          </Grid>
        )}
        {!stateTransition.isInitial && (
          <Grid item xs={6} md={6} lg={3}>
            <Autocomplete
              fullWidth
              value={stateTransition.fromStateId}
              simpleValue
              onChange={value => onPropertyChanged('fromStateId', value)}
              options={documentStateList}
              label={t('DocumentTypes.DocumentStateTransition.FromStatus')}
              error={!(isValid(validation.fromStateId) || stateTransition.isInitial)}
              helperText={getErrors(validation.fromStateId)}
              isSearchable={true}
            />
          </Grid>
        )}
        {!stateTransition.isFinal && (
          <Grid item xs={6} md={6} lg={3}>
            <Autocomplete
              fullWidth
              value={stateTransition.toStateId}
              simpleValue
              onChange={value => onPropertyChanged('toStateId', value)}
              options={documentStateList}
              label={t('DocumentTypes.DocumentStateTransition.ToStatus')}
              error={!(isValid(validation.toStateId) || stateTransition.isFinal)}
              helperText={getErrors(validation.toStateId)}
              isSearchable={true}
            />
          </Grid>
        )}
        <Grid item xs={6} md={6} lg={3}>
          <Tooltip
            title={t('DocumentTypes.DocumentStateTransition.InitialTransitionExists')}
            disableHoverListener={!transitionsCheck.initialTransitionExists}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={stateTransition.isInitial || false}
                  onChange={event => onPropertyChanged('isInitial', event.target.checked)}
                  disabled={stateTransition.isFinal || transitionsCheck.initialTransitionExists}
                />
              }
              label={t('DocumentTypes.DocumentStateTransition.Initial')}
              labelPlacement='end'
            />
          </Tooltip>
        </Grid>
        <Grid item xs={6} md={6} lg={3}>
          <Tooltip
            title={t('DocumentTypes.DocumentStateTransition.FinalTransitionExists')}
            disableHoverListener={!transitionsCheck.finalTransitionExists}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={stateTransition.isFinal || false}
                  onChange={event => onPropertyChanged('isFinal', event.target.checked)}
                  disabled={stateTransition.isInitial || transitionsCheck.finalTransitionExists}
                />
              }
              label={t('DocumentTypes.DocumentStateTransition.Final')}
              labelPlacement='end'
            />
          </Tooltip>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6} md={6} lg={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={stateTransition.saveImage || false}
                onChange={event => onPropertyChanged('saveImage', event.target.checked)}
              />
            }
            label={t('DocumentTypes.DocumentStateTransition.SaveImage')}
            labelPlacement='end'
          />
        </Grid>
        <Grid item xs={6} md={6} lg={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={stateTransition.notifyUser || false}
                onChange={event => onPropertyChanged('notifyUser', event.target.checked)}
              />
            }
            label={t('DocumentTypes.DocumentStateTransition.NotifyUser')}
            labelPlacement='end'
          />
        </Grid>
        <Grid item xs={6} md={6} lg={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={stateTransition.notifyResponsible || false}
                onChange={event => onPropertyChanged('notifyResponsible', event.target.checked)}
              />
            }
            label={t('DocumentTypes.DocumentStateTransition.NotifyResponsible')}
            labelPlacement='end'
          />
        </Grid>
        <Grid item xs={6} md={6} lg={3}>
          <Autocomplete
            id={'userGroupNotification'}
            fullWidth
            isMultiSelection
            withCheckboxes
            size='small'
            simpleValue={true}
            labelKey={'name'}
            label={t('DocumentTypes.DocumentStateTransition.UserGroupsNotification')}
            value={stateTransition.userGroupsNotification?.map(item => {
              return item?.userGroupId;
            })}
            valueKey={'id'}
            options={userGroups}
            onChange={onUserGroupChanged}
            isClearable={true}
            isSearchable={true}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6} md={6} lg={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={stateTransition.requiresConfirmation || false}
                onChange={event => {
                  stateTransition.textResourceId = event.target.checked ? stateTransition.textResourceId : null;
                  onPropertyChanged('requiresConfirmation', event.target.checked);
                }}
              />
            }
            label={t('DocumentTypes.DocumentStateTransition.RequiresConfirmation')}
            labelPlacement='end'
          />
        </Grid>
        {stateTransition.requiresConfirmation && (
          <Grid container wrap='nowrap' item xs={6} md={6} lg={3}>
            <Autocomplete
              fullWidth
              value={stateTransition.textResourceId}
              simpleValue
              onChange={value => onPropertyChanged('textResourceId', value)}
              getOptionLabel={option => option.code}
              options={textResourceCodeList}
              label={t('DocumentTypes.DocumentStateTransition.TextResourceCode')}
              isSearchable={true}
            />
            <IconButton color='transparent' tooltip={t('DocumentTypes.DocumentStateTransition.TextResourceCodeInformation')} disabled>
              <Info />
            </IconButton>
          </Grid>
        )}
      </Grid>
    </Card >
  );
};

StateTransitionComponent.propTypes = {
  onPropertyChanged: PropTypes.func.isRequired,
  onUserGroupChanged: PropTypes.func.isRequired,
  stateTransition: PropTypes.object,
  documentStateList: PropTypes.array,
  textResourceCodeList: PropTypes.array,
  userGroups: PropTypes.array,
  validation: PropTypes.object.isRequired,
  transitionsCheck: PropTypes.object
};

export default StateTransitionComponent;
